import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridEventListener } from '@mui/x-data-grid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { admissionMethodApi } from '../../../services';
import { useErrorHandler } from '../../../lib/errorHandler';
import { useAppSelector } from '../../../store/hooks';
import { AdmissionMethodSchema } from '../../../services/admissionMethodApi';
import { z } from 'zod';
import { MOBILE_BREAKPOINT, ROW_HEIGHTS, LIST_HEIGHTS } from '../../../utils/responsive';
import { useEntityListState, createEntityToolbar, type StatsQueryParams } from '../../../hooks';

type AdmissionMethod = z.infer<typeof AdmissionMethodSchema>;
import { BaseDataGrid, PageHeader, DeleteConfirmDialog, PersistentTabs, TabConfig } from '../../../components';
import { AdmissionMethodsLineChart } from './AdmissionMethodsLineChart';

const AdmissionMethodsToolbar = createEntityToolbar({
	searchPlaceholder: 'Tìm kiếm phương thức xét tuyển...',
	exportFn: (params) => admissionMethodApi.exportToExcel(params),
});
import { AdmissionMethodsBarChart } from './AdmissionMethodsBarChart';
import { AdmissionMethodsByMajorTab } from './AdmissionMethodsByMajorTab';
import { useAdmissionMethodsColumns, useAdmissionMethodsQuery } from '../hooks';
import { AdmissionMethodDialog } from '../../../components/Dialogs';

interface AdmissionMethodsContentProps {
	isAdmin: boolean;
	canEdit: boolean;
}

export default function AdmissionMethodsContent({ isAdmin, canEdit }: AdmissionMethodsContentProps) {
	const { showError, showSuccess } = useErrorHandler();
	const queryClient = useQueryClient();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);

	const {
		searchValue, paginationModel, sortModel, filterModel,
		handlePaginationModelChange, handleSortModelChange,
		handleFilterModelChange, handleSearch,
	} = useEntityListState({ defaultSortField: 'tenPhuongThuc' });

	// Dialog states
	const [admissionMethodDialog, setAdmissionMethodDialog] = useState<{
		open: boolean;
		mode: 'create' | 'edit' | 'view';
		admissionMethod: AdmissionMethod | null;
	}>({ open: false, mode: 'create', admissionMethod: null });

	const [deleteDialog, setDeleteDialog] = useState<{
		open: boolean;
		admissionMethod: AdmissionMethod | null;
	}>({ open: false, admissionMethod: null });

	// Major filter for dialog stats (used by "Theo ngành" tab)
	const [dialogMaNganh, setDialogMaNganh] = useState<string | null>(null);

	// Callback functions for columns
	const handleEdit = useCallback(
		(admissionMethod: AdmissionMethod) => () => {
			setSearchParams({ action: 'edit', id: admissionMethod.maPhuongThuc });
		},
		[setSearchParams],
	);

	const handleView = useCallback(
		(admissionMethod: AdmissionMethod) => () => {
			setSearchParams({ action: 'view', id: admissionMethod.maPhuongThuc });
		},
		[setSearchParams],
	);

	const handleDelete = useCallback(
		(admissionMethod: AdmissionMethod) => () => {
			setDeleteDialog({
				open: true,
				admissionMethod,
			});
		},
		[],
	);

	// Use query hook with pagination and sorting
	const queryParams: StatsQueryParams = {
		filters: {
			query: searchValue,
			maKhoa: selectedFaculty ? [selectedFaculty] : undefined
		},
		page: paginationModel.page,
		pageSize: paginationModel.pageSize,
		sortBy: sortModel.length > 0 ? sortModel[0].field : undefined,
		sortOrder: (sortModel.length > 0 ? sortModel[0].sort : undefined) as 'asc' | 'desc' | undefined,
	};
	const { data: admissionMethodsData, isLoading, refetch } = useAdmissionMethodsQuery(queryParams);
	const exportParams = useAdmissionMethodsQuery.buildExportParams(queryParams);

	const admissionMethods = useMemo(() => admissionMethodsData?.data?.stats || [], [admissionMethodsData?.data?.stats]);
	const totalCount = admissionMethodsData?.pagination?.total || 0;

	// Track dialog state with ref to avoid stale closures in useEffect
	const dialogRef = useRef(admissionMethodDialog);
	dialogRef.current = admissionMethodDialog;

	// Sync URL params with dialog state
	useEffect(() => {
		const action = searchParams.get('action') as 'create' | 'edit' | 'view' | null;
		const methodId = searchParams.get('id');

		// Security: Redirect unauthorized users from edit/create to view mode
		if (action && ['create', 'edit'].includes(action) && !canEdit) {
			if (methodId) {
				setSearchParams({ action: 'view', id: methodId });
			} else {
				setSearchParams({});
			}
			return;
		}

		if (action && ['create', 'edit', 'view'].includes(action)) {
			if (action === 'create') {
				if (!dialogRef.current.open || dialogRef.current.mode !== 'create') {
					setAdmissionMethodDialog({
						open: true,
						mode: 'create',
						admissionMethod: null,
					});
				}
			} else if (methodId && admissionMethods.length > 0) {
				// Skip if dialog is already showing the same entity in the same mode
				if (dialogRef.current.open
					&& dialogRef.current.admissionMethod?.maPhuongThuc === methodId
					&& dialogRef.current.mode === action) {
					return;
				}
				const method = admissionMethods.find((m) => m.maPhuongThuc === methodId);
				if (method) {
					setAdmissionMethodDialog({
						open: true,
						mode: action,
						admissionMethod: method as unknown as AdmissionMethod,
					});
				}
			}
		} else if (!action && dialogRef.current.open) {
			setAdmissionMethodDialog({ open: false, mode: 'create', admissionMethod: null });
		}
	}, [searchParams, admissionMethods, canEdit, setSearchParams]);

	// Delete mutation
	const deleteMutation = useMutation({
		mutationFn: admissionMethodApi.deleteAdmissionMethod,
		onSuccess: () => {
			showSuccess('Xóa phương thức tuyển sinh thành công');
			queryClient.invalidateQueries({ queryKey: ['admissionMethods'] });
			setDeleteDialog({ open: false, admissionMethod: null });
		},
		onError: (error: Error) => {
			showError(error.message || 'Có lỗi xảy ra khi xóa phương thức tuyển sinh');
		},
	});

	const handleRefresh = useCallback(() => {
		// Prevent multiple rapid calls
		if (isLoading) return;

		refetch();
	}, [isLoading, refetch]);

	const handleRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
		if (canEdit) {
			handleEdit(params.row as AdmissionMethod)();
		} else {
			handleView(params.row as AdmissionMethod)();
		}
	}, [canEdit, handleEdit, handleView]);

	const handleCreate = useCallback(() => {
		setSearchParams({ action: 'create' });
	}, [setSearchParams]);

	const handleDeleteConfirm = useCallback(() => {
		// Prevent double submission
		if (deleteMutation.isPending || !deleteDialog.admissionMethod) return;

		deleteMutation.mutate(deleteDialog.admissionMethod.maPhuongThuc);
	}, [deleteDialog.admissionMethod, deleteMutation]);

	const handleCloseAdmissionMethodDialog = useCallback(() => {
		if (searchParams.has('action')) {
			setSearchParams({});
		}
		setAdmissionMethodDialog({ open: false, mode: 'create', admissionMethod: null });
		setDialogMaNganh(null);
	}, [searchParams, setSearchParams]);

	// Callback for "Theo ngành" tab row click — opens dialog in view mode with major filter
	const handleViewMethodByMajor = useCallback(
		(method: AdmissionMethod, maNganh?: string) => {
			setDialogMaNganh(maNganh ?? null);
			setAdmissionMethodDialog({ open: true, mode: 'view', admissionMethod: method });
		},
		[],
	);

	const handleCloseDeleteDialog = useCallback(() => {
		setDeleteDialog({ open: false, admissionMethod: null });
	}, []);

	const columns = useAdmissionMethodsColumns({
		isAdmin,
		isMobile,
		admissionMethodStats: admissionMethodsData?.data,
		onEdit: handleEdit,
		onDelete: handleDelete
	});

	const tabs: TabConfig[] = [
		{
			label: 'Danh sách',
			content: (
				<Box sx={{
					flex: 1,
					minHeight: 0,
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					display: 'flex',
					flexDirection: 'column'
				}}>
					<BaseDataGrid
						rows={admissionMethods}
						columns={columns}
						loading={isLoading}
						onRowClick={handleRowClick}
						getRowId={(row) => row.maPhuongThuc}
						rowCount={totalCount}
						paginationModel={paginationModel}
						onPaginationModelChange={handlePaginationModelChange}
						sortModel={sortModel}
						onSortModelChange={handleSortModelChange}
						filterModel={filterModel}
						onFilterModelChange={handleFilterModelChange}
						pageSizeOptions={[10, 25, 50, 100]}
						paginationMode="server"
						sortingMode="server"
						filterMode="server"
						disableRowSelectionOnClick
						rowHeight={isMobile ? undefined : ROW_HEIGHTS.DESKTOP}
						getRowHeight={isMobile ? () => 'auto' : undefined}
						slots={{
							toolbar: () => (
								<AdmissionMethodsToolbar
									searchValue={searchValue}
									onSearch={handleSearch}
									onRefresh={handleRefresh}
									exportParams={exportParams}
									isMobile={isMobile}
								/>
							),
						}}
					/>
				</Box>
			)
		},
		{
			label: 'Biểu đồ đường',
			content: (
				<Box sx={{
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					overflow: 'auto'
				}}>
					<AdmissionMethodsLineChart height={500} />
				</Box>
			)
		},
		{
			label: 'Biểu đồ cột',
			content: (
				<Box sx={{
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					overflow: 'auto'
				}}>
					<AdmissionMethodsBarChart height={500} />
				</Box>
			)
		},
		{
			label: 'Theo ngành',
			content: (
				<AdmissionMethodsByMajorTab onViewMethod={handleViewMethodByMajor} />
			)
		}
	];

	return (
		<Box sx={{
			width: '100%',
			height: '100vh',
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',
			p: 0
		}}>
			<PageHeader
				title="Danh sách Phương thức tuyển sinh"
				onRefresh={handleRefresh}
				onAdd={canEdit ? handleCreate : undefined}
				isAdmin={isAdmin}
				loading={isLoading}
			/>

			<PersistentTabs
				pageKey="admissionMethods"
				tabs={tabs}
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					minHeight: 0
				}}
			/>

			<AdmissionMethodDialog
				open={admissionMethodDialog.open}
				onClose={handleCloseAdmissionMethodDialog}
				admissionMethod={admissionMethodDialog.admissionMethod}
				mode={admissionMethodDialog.mode}
				maNganh={dialogMaNganh}
			/>

			<DeleteConfirmDialog
				open={deleteDialog.open}
				onClose={handleCloseDeleteDialog}
				onConfirm={handleDeleteConfirm}
				title="Xóa phương thức tuyển sinh"
				message={`Bạn có chắc chắn muốn xóa phương thức tuyển sinh "${deleteDialog.admissionMethod?.tenPhuongThuc || deleteDialog.admissionMethod?.maPhuongThuc}"? Hành động này không thể hoàn tác.`}
				isLoading={deleteMutation.isPending}
			/>
		</Box>
	);
}